import { useEffect, useId } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  schema?: object | object[];
}

/**
 * Per-page SEO component.
 * React 19 hoists <title>, <meta>, <link> to <head> automatically.
 * JSON-LD schema is injected via useEffect to ensure safe server-free hydration.
 */
export default function SEO({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  schema,
}: SEOProps) {
  const siteUrl = import.meta.env.VITE_SITE_URL ?? '';
  const canonicalUrl = canonical ? `${siteUrl}${canonical}` : undefined;
  const schemaId = useId();

  useEffect(() => {
    if (!schema) return;
    const schemas = Array.isArray(schema) ? schema : [schema];
    const tags: HTMLScriptElement[] = schemas.map((s) => {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.id = `schema-${schemaId}-${schemas.indexOf(s)}`;
      el.text = JSON.stringify(s);
      document.head.appendChild(el);
      return el;
    });
    return () => {
      tags.forEach((el) => {
        if (el.parentNode) el.parentNode.removeChild(el);
      });
    };
  }, [schema, schemaId]);

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
    </>
  );
}
